import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Divider,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useTheme, useMediaQuery } from "@mui/material";
import { useCognito } from "../../../shared/auth/cognito-react";
import { useLocation, useNavigate } from "react-router-dom";
import PaymentAmount from "./components/PaymentAmount";
import PaymentTotal from "./components/PaymentTotal";
import PaymentRecurrence from "./components/PaymentRecurrence";
import PayNearMePayment from "./components/PayNearMePayment";
import {
  getAutoPaymentInfo,
  getPaymentInfo,
  getPaymentMethods,
  postPayment,
  postPaymentQuote,
  postRecurringPayment,
  putRecurringPayment,
} from "../../../data/paymentsAPI";
import { ThemeContext } from "../../../contexts/ThemeContext";
import { AuthContext } from "../../../contexts/AuthContext";
import { ConfiguratorContext } from "../../../contexts/ConfiguratorContext";
import {
  FlexContainer,
  CardMedium,
} from "../../../shared/components/styled/Container.styled";
import { SectionTitle } from "../../../shared/components/styled/Typography.styled";
import { PillButton } from "../../../shared/components/styled/Button.styled";
import FormItem from "../../../shared/components/form/FormItem";
import ConfirmationDialog from "../../../shared/components/ConfirmationDialog";
import ActionSnackbar from "../../../shared/components/ActionSnackbar";
import EditableText from "../../../shared/components/configurator/EditableText";
import EditableTermsAndConditions from "../../../shared/components/configurator/EditableTermsAndConditions";
import {
  EMPTY_PAYMENT_METHODS_FALLBACK,
  ESCROW_SHORTAGE,
  MONTHS,
  NEW_PAYMENT_DELINQUENT_PAYMENT_NOT_ALLOWED_WARNING_MESSAGE,
  NEW_PAYMENT_ONLINE_PAYMENT_NOT_ALLOWED_WARNING_MESSAGE,
  NEW_PAYMENT_PREVALIDATE_ONE_TIME_PAYMENT_NOT_ALLOWED_WARNING_MESSAGE,
  NEW_PAYMENT_PREVALIDATE_RECURRING_PAYMENT_NOT_ALLOWED_WARNING_MESSAGE,
  NEW_PAYMENT_RECURRING_PAYMENT_NOT_ALLOWED_WARNING_MESSAGE,
  ONE_TIME_PAYMENT,
  PAYMENT_CONFIRMATION,
  PAYMENT_METHOD_FOOTER_NOTE,
  PAYMENT_SUCCESSFUL,
  PNM_SMART_LINK_URL_LABEL,
  getPnmSmartLinkNotFoundMessage,
  RECURRING_PAYMENT,
} from "../../../shared/constants/Payments.constants";
import {
  AUTO_PAYMENTS_ONLINE_ALLOW_AUTO_DEBIT_KEY,
  AUTO_PAYMENTS_ONLINE_ALLOW_DELINQUENT_LOANS_KEY,
  AUTO_PAYMENTS_ONLINE_EARLIEST_DAY_AUTO_DEBIT_KEY,
  AUTO_PAYMENTS_ONLINE_LATEST_DAY_AUTO_DEBIT_KEY,
  NEW_ESCROW_SHORTAGE_PAGE_TERMS_AND_CONDITIONS_KEY,
  NEW_ONE_TIME_PAYMENT_PAGE_TERMS_AND_CONDITIONS_KEY,
  NEW_PAYMENT_PAGE_DESCRIPTION_KEY,
  NEW_PAYMENT_PAGE_FOOTER_NOTE_KEY,
  NEW_RECURRING_PAYMENT_PAGE_TERMS_AND_CONDITIONS_KEY,
  PAYMENTS_MANDATE_LATE_FEE_KEY,
  PAYMENTS_ONLINE_ALLOW_PRINCIPAL_IF_DELINQUENT_KEY,
  PAYMENTS_ONLINE_ALLOWED_KEY,
  PAYMENTS_ONLINE_CUSTOMER_TRANSACTION_KEY,
  PAYMENTS_ONLINE_CUSTOMER_TRANSACTION_MAX_KEY,
  PAYMENTS_ONLINE_CUTOFF_TIME_KEY,
  PAYMENTS_ONLINE_DAYS_UNTIL_DELINQUENT_KEY,
  PAYMENTS_ONLINE_FEE_KEY,
  PAYMENTS_ONLINE_FEE_OPTION_1_KEY,
  PAYMENTS_ONLINE_FEE_OPTION_2_KEY,
  PAYMENTS_ONLINE_FEE_OPTION_3_KEY,
  PAYMENTS_ONLINE_FEE_OPTION_4_KEY,
  PAYMENTS_ONLINE_FEE_OPTION_KEY,
  PAYMENTS_ONLINE_LATE_CHARGE_THRESHOLD_KEY,
  PAYMENTS_ONLINE_LATE_FEE_FILL_KEY,
  PAYMENTS_ONLINE_MAX_DAYS_INTO_FUTURE_KEY,
  PAYMENTS_ONLINE_OTHER_FEE_FILL_KEY,
  PAYMENTS_ONLINE_PAYMENT_AMOUNT_FILL_KEY,
  PAYMENTS_ONLINE_POSTING_DATE_KEY,
  PAYMENTS_ONLINE_PRINCIPAL_TRANSACTION_MAX_KEY,
  PAYMENTS_ONLINE_STATE_FEE_MAX_KEY,
  PAYMENTS_ONLINE_STATE_TRANSACTION_MAX_KEY,
  PAYMENTS_ONLINE_FEE_CLIENT_ROUTING_NO_KEY,
} from "../../../shared/constants/Configurator.constants";
import {
  capitalizeFirstLetterOnly,
  convertTwentyFourToTwelveHourFormat,
  createDaysDropdownValues,
  findElementIncludedInArray,
  getDisplayLoanNumber,
  getLoanId,
  isValidPnmSmartLink,
} from "../../../shared/utils/utility-functions";
import { useIsMount } from "../../../shared/hooks/useIsMount";
import { usePnmIntegrationConfig } from "../../../shared/hooks/usePnmIntegrationConfig";
import {
  addBusinessDays,
  addDays,
  differenceInCalendarDays,
  eachDayOfInterval,
  isSameDay,
  isWeekend,
  startOfMonth,
} from "date-fns";
import { getHolidays } from "../../../data/holidayAPI";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { CLIENT_ADMIN_ROLE } from "../../../shared/constants/Roles.constants";
import { NO_LOAN_REGISTERED_WARNING_MESSAGE } from "../../../shared/constants/UserProfile.constants";
import {
  GENERIC_ERROR_MESSAGE,
  TOP_CENTER_SNACKBAR_WIDTH,
} from "../../../shared/constants/Defaults.constants";
import {
  AdminAutoPaymentPrevalidateResponse,
  AdminPaymentPrevalidateResponse,
} from "../../../mocks/Admin.mock";
import { formatCurrency } from "../../../shared/utils/currency";

// Long enough that typing an amount does not fire a quote per character, short enough
// that the fee has settled by the time the borrower reaches the submit button.
const FEE_QUOTE_DEBOUNCE_MS = 400;

// The service maps this label to its PaymentType enum and rejects anything it does not
// recognise, and the portal's own constant is "Recurring" with a capital R.
const RECURRING_PAYMENT_TYPE_LABEL = "recurring";

export default function NewPayment() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isFirstRender = useIsMount();
  const { instance, accounts } = useCognito();
  const { state } = useLocation();
  const { addPendingRequest, removePendingRequest } = useContext(ThemeContext);
  const {
    token,
    userRoles,
    visibleRole,
    activeLoan,
    activeLoanDetails,
    impersonatedUser,
  } = useContext(AuthContext);
  const { edittedFields } = useContext(ConfiguratorContext);
  const { pnmIntegrationEnabled } = usePnmIntegrationConfig();
  const escrowShortage = activeLoanDetails?.loanDetails?.escrowShortage;
  const escrowShortageAmount =
    activeLoanDetails?.loanDetails?.escrowShortageAmount;
  const webPaymentOverride = Boolean(
    activeLoanDetails?.loanDetails?.webPaymentOverride,
  );
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const today = new Date();

  // Dropdown States
  const [type, setType] = useState(ONE_TIME_PAYMENT);
  const [mode, setMode] = useState("new");
  const rawPnmSmartlink = activeLoanDetails?.borrowerDetails?.pnmSmartlink;
  const pnmSmartlink =
    typeof rawPnmSmartlink === "string" ? rawPnmSmartlink.trim() : "";
  const isPnmSmartlinkEmpty =
    rawPnmSmartlink == null ||
    rawPnmSmartlink === "" ||
    (typeof rawPnmSmartlink === "string" && rawPnmSmartlink.trim() === "");
  const isNewMakePaymentFlow =
    mode !== "edit" && state?.from !== "activity";
  const shouldUsePnmMakePaymentView =
    pnmIntegrationEnabled && isNewMakePaymentFlow;
  // PAYMENTS_PNM_INTEGRATION_ALLOWED=false → default payment form (see final return)
  const showPnmMissingSmartlinkView =
    shouldUsePnmMakePaymentView && isPnmSmartlinkEmpty;
  const showPnmPaymentView =
    shouldUsePnmMakePaymentView &&
    !isPnmSmartlinkEmpty &&
    isValidPnmSmartLink(pnmSmartlink);
  const [pnmLoadError, setPnmLoadError] = useState(false);
  const [availableDays, setAvailableDays] = useState([]);
  const [unavailableDays, setUnavailableDays] = useState({});
  const [selectedDay, setSelectedDay] = useState(1);
  const [selectedMonth, setSelectedMonth] = useState(1);
  const [selectedMethod, setSelectedMethod] = useState("");
  const [paymentMethods, setPaymentMethods] = useState([]);

  // Due Amounts States
  const [amountDue, setAmountDue] = useState(0);
  const [lateFeesDue, setLateFeesDue] = useState(0);
  const [feesDue, setFeesDue] = useState(0);
  const [dueDate, setDueDate] = useState(today);

  // Field States
  const [amount, setAmount] = useState("");
  const [lateFees, setLateFees] = useState("");
  const [fees, setFees] = useState("");
  const [additionalPrincipal, setAdditionalPrincipal] = useState("");
  const [additionalEscrow, setAdditionalEscrow] = useState("");

  // Total States
  const [hasTransactionFee, setHasTransactionFee] = useState(false);
  const [transactionFeeOption, setTransactionFeeOption] = useState("1");
  const [transactionFee, setTransactionFee] = useState(0);
  const [feeQuoteFailed, setFeeQuoteFailed] = useState(false);

  // True from the moment an input changes until the quote for it lands, covering the
  // debounce as well as the request. The fee on screen during that window belongs to
  // the previous set of inputs, and submitting against it would have the borrower
  // agree to a number that is already out of date.
  const [feeQuotePending, setFeeQuotePending] = useState(false);

  // The service's own words for why it rejected the payment. Quoting runs the whole
  // validation chain, so a quote can fail because the payment is invalid rather than
  // because the service is unreachable, and the borrower needs to be told which.
  // Kept apart from warningMessage, which its own effect clears and rebuilds.
  const [feeQuoteMessage, setFeeQuoteMessage] = useState("");
  const [amountTotal, setAmountTotal] = useState(0);

  // Allow Payment States
  const [paymentsOnlineAllowed, setPaymentsOnlineAllowed] = useState(true);
  const [cutoffHourPaymentAllowed, setCutoffHourPaymentAllowed] =
    useState(true);
  const [delinquentLoanPaymentAllowed, setDelinquentLoanPaymentAllowed] =
    useState(true);
  const [delinquentLoanPrincipalAllowed, setDelinquentLoanPrincipalAllowed] =
    useState(true);
  const [additionalEscrowAmountAllowed, setAdditionalEscrowAmountAllowed] =
    useState(true);
  const [autoPaymentDueDate, setAutoPaymentDueDate] = useState(new Date());

  // Configurator States
  const [footerNote, setFooterNote] = useState(PAYMENT_METHOD_FOOTER_NOTE);
  const [description, setDescription] = useState("");
  const [termsAndConditionsText, setTermsAndConditionsText] = useState("");
  const [termsAndConditionsElementKey, setTermsAndConditionsElementKey] =
    useState("");
  const [
    oneTimePaymentTermsAndConditionsText,
    setOneTimePaymentTermsAndConditionsText,
  ] = useState("");
  const [
    recurringPaymentTermsAndConditionsText,
    setRecurringPaymentTermsAndConditionsText,
  ] = useState("");
  const [
    escrowShortagePaymentTermsAndConditionsText,
    setEscrowShortageTermsAndConditionsText,
  ] = useState("");
  const [termsAndConditionsCheckbox, setTermsAndConditionsCheckbox] =
    useState(false);
  const [recurringPaymentsOnlineAllowed, setRecurringPaymentsOnlineAllowed] =
    useState(false);
  const [paymentAmountFill, setPaymentAmountFill] = useState(0);
  const [lateFeeAmountFill, setLateFeeAmountFill] = useState(0);
  const [feeAmountFill, setFeeAmountFill] = useState(0);
  const [customerMaxTransaction, setCustomerMaxTransaction] = useState(null);
  const [principalMaxTransaction, setPrincipalMaxTransaction] = useState(null);
  const [stateMaxTransaction, setStateMaxTransaction] = useState(null);
  const [stateMaxFee, setStateMaxFee] = useState(null);
  const [mandateLateFee, setMandateLateFee] = useState(false);
  const [
    allowAutoDebitForDelinquentLoans,
    setAllowAutoDebitForDelinquentLoans,
  ] = useState(true);
  const [delinquentLoanAutoDebitAllowed, setDelinquentLoanAutoDebitAllowed] =
    useState(true);
  const [isPrincipalOnlyPayment, setIsPrincipalOnlyPayment] = useState(false);
  const [cutoffTime, setCutoffTime] = useState("20:00");
  const [postingDateDays, setPostingDateDays] = useState(0);
  const [delinquentDays, setDelinquentDays] = useState("");
  const [lateChargeThreshold, setLateChargeThreshold] = useState(0);
  const [principalIfDelinquent, setPrincipalIfDelinquent] = useState(true);
  const [maxDaysIntoFutureForPayment, setMaxDaysIntoFutureForPayment] =
    useState(35);
  const [paymentFeeRoutingNo, setPaymentFeeRoutingNo] = useState("");

  // Error States
  const [amountErrorMessage, setAmountErrorMessage] = useState("");
  const [lateFeesErrorMessage, setLateFeesErrorMessage] = useState("");
  const [feesErrorMessage, setFeesErrorMessage] = useState("");
  const [additionalPrincipalErrorMessage, setAdditionalPrincipalErrorMessage] =
    useState("");
  const [additionalEscrowErrorMessage, setAdditionalEscrowErrorMessage] =
    useState("");

  // Warning States
  const [warningMessage, setWarningMessage] = useState("");

  // Datepicker States
  const [startDate, setStartDate] = useState(today);
  const [minDate, setMinDate] = useState(today);
  const [maxDate, setMaxDate] = useState(today);
  const [datepickerOpen, setDatepickerOpen] = useState(false);
  const [excludedDates, setExcludedDates] = useState([]);

  // Transaction Date States
  const [earliestAutoDebitDate, setEarliestAutoDebitDate] = useState(1);
  const [latestAutoDebitDate, setLatestAutoDebitDate] = useState(23);

  // Dialog States
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmationDialogBody, setConfirmationDialogBody] = useState("");

  // Snackbar States
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState(
    NO_LOAN_REGISTERED_WARNING_MESSAGE,
  );
  const [snackbarSeverity, setSnackbarSeverity] = useState("warning");

  // Disabled States
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [oneTimePaymentAllowed, setOneTimePaymentAllowed] = useState(true);
  const [recurringPaymentAllowed, setRecurringPaymentAllowed] = useState(true);
  const [disabledPaymentMethod, setDisabledPaymentMethod] = useState(true);
  const [helocOrConstructionLoan, setHelocOrConstructionLoan] = useState(false);

  const [openPaymentConfirmation, setOpenPaymentConfirmation] = useState(false);
  const [paymentConfirmationNumber, setPaymentConfirmationNumber] = useState(0);

  useEffect(() => {
    const determineMaxDate = async () => {
      if (token && !isNaN(maxDaysIntoFutureForPayment)) {
        const holidayArray = await getHolidays(
          token,
          +maxDaysIntoFutureForPayment + 1,
          impersonatedUser,
        );
        const holidayDates = holidayArray.map((item) =>
          utcToZonedTime(item, userTimeZone),
        );
        setExcludedDates(holidayDates);
        setMaxDate(addDays(today, maxDaysIntoFutureForPayment));
      }
    };
    determineMaxDate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, maxDaysIntoFutureForPayment]);

  useEffect(() => {
    if (state?.type) {
      setType(state.type);
    }
    if (state?.from === "activity") {
      setMode("edit");
    }
  }, [state]);

  useEffect(() => {
    if (!isFirstRender) {
      setPaymentTypeAllowed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentsOnlineAllowed, recurringPaymentsOnlineAllowed]);

  useEffect(() => {
    // Clear any existing warning messages first
    setWarningMessage("");

    // Check if online payments are allowed
    if (!paymentsOnlineAllowed) {
      setWarningMessage(NEW_PAYMENT_ONLINE_PAYMENT_NOT_ALLOWED_WARNING_MESSAGE);
      return;
    }

    // Check recurring payment permissions only if type is recurring
    if (type === RECURRING_PAYMENT) {
      if (!recurringPaymentsOnlineAllowed) {
        setWarningMessage(
          NEW_PAYMENT_RECURRING_PAYMENT_NOT_ALLOWED_WARNING_MESSAGE,
        );
        return;
      }
      if (!recurringPaymentAllowed) {
        setWarningMessage(
          NEW_PAYMENT_PREVALIDATE_RECURRING_PAYMENT_NOT_ALLOWED_WARNING_MESSAGE,
        );
        return;
      }
    }

    // Check one-time payment permissions only if type is not recurring
    if (type !== RECURRING_PAYMENT) {
      if (!oneTimePaymentAllowed) {
        setWarningMessage(
          NEW_PAYMENT_PREVALIDATE_ONE_TIME_PAYMENT_NOT_ALLOWED_WARNING_MESSAGE,
        );
        return;
      }
    }

    // Check delinquent loan payment permissions
    if (!delinquentLoanPaymentAllowed || !delinquentLoanAutoDebitAllowed) {
      setWarningMessage(
        NEW_PAYMENT_DELINQUENT_PAYMENT_NOT_ALLOWED_WARNING_MESSAGE,
      );
      return;
    }

    // Check cutoff hour payment permissions
    if (!cutoffHourPaymentAllowed) {
      setWarningMessage(
        `Payments made after ${convertTwentyFourToTwelveHourFormat(
          cutoffTime,
        )} will not be processed until the following business day`,
      );
    }
  }, [
    oneTimePaymentAllowed,
    recurringPaymentAllowed,
    paymentsOnlineAllowed,
    recurringPaymentsOnlineAllowed,
    delinquentLoanPaymentAllowed,
    delinquentLoanAutoDebitAllowed,
    cutoffHourPaymentAllowed,
    cutoffTime,
    type,
  ]);

  useEffect(() => {
    resetAdditionalFields();
  }, [type]);

  useEffect(() => {
    if (
      token &&
      (activeLoan === "no-loan" ||
        activeLoan === activeLoanDetails?.loanDetails?.loanId)
    ) {
      resetAdditionalFields();
      fillConfiguratorFields(edittedFields);
      if (!shouldUsePnmMakePaymentView) {
        callPaymentInfoApi(token);

        if (
          !edittedFields.find(
            (item) => item.key === PAYMENTS_ONLINE_CUTOFF_TIME_KEY,
          )
        ) {
          checkPaymentTime(cutoffTime);
        }
      }
    }
    if (
      activeLoanDetails?.loanDetails?.mortgageBalance === 0 &&
      (activeLoanDetails?.loanDetails?.helocLoan ||
        activeLoanDetails?.loanDetails?.constructionLoan)
    ) {
      setHelocOrConstructionLoan(true);
    } else {
      setHelocOrConstructionLoan(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, instance, token, activeLoan, activeLoanDetails]);

  useEffect(() => {
    const configuratorCutoffTime = edittedFields.find(
      (item) => item.key === PAYMENTS_ONLINE_CUTOFF_TIME_KEY,
    );
    if (configuratorCutoffTime) {
      checkPaymentTime(configuratorCutoffTime.value);
    } else {
      checkPaymentTime(cutoffTime);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate]);

  useEffect(() => {
    setAvailableDays(
      createDaysDropdownValues(
        earliestAutoDebitDate,
        latestAutoDebitDate,
        "recurring-payment-starting-day",
      ),
    );

    let selectedDay = earliestAutoDebitDate;
    let selectedMonth =
      MONTHS[today.getMonth() !== 11 ? today.getMonth() + 1 : 0].value;
    if (state && state.postingDate) {
      const dateSplit = state.postingDate.split("/");
      selectedDay =
        +dateSplit[1] < earliestAutoDebitDate
          ? earliestAutoDebitDate
          : +dateSplit[1] > latestAutoDebitDate
          ? latestAutoDebitDate
          : +dateSplit[1];
      selectedMonth = MONTHS[dateSplit[0] - 1].value;
    }

    const threeDaysIntervalDates = eachDayOfInterval({
      start: today,
      end: addDays(today, 2),
    });

    const previousDaysIntervalDates = eachDayOfInterval({
      start: startOfMonth(today),
      end: today,
    });

    const unavailableDaysInterval = previousDaysIntervalDates.concat(
      threeDaysIntervalDates,
    );

    const unavailableDaysIntervalNoDupes = Array.from(
      new Set(unavailableDaysInterval.map((item) => item.valueOf())),
    ).map((item) => new Date(item));

    const recurringPaymentDisabledDates = {};

    unavailableDaysIntervalNoDupes.forEach((item) => {
      if (!recurringPaymentDisabledDates[item.getMonth() + 1]) {
        recurringPaymentDisabledDates[item.getMonth() + 1] = [];
      }
      recurringPaymentDisabledDates[item.getMonth() + 1].push(item.getDate());
    });

    setUnavailableDays(recurringPaymentDisabledDates);
    setSelectedDay(selectedDay);
    setSelectedMonth(selectedMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [earliestAutoDebitDate, latestAutoDebitDate]);

  useEffect(() => {
    prefillPostingDate(postingDateDays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postingDateDays]);

  useEffect(() => {
    checkDelinquentPaymentAllowed();
    checkDelinquentPrincipalAllowed();
    checkDelinquentRecurringAllowed();
    checkAdditionalEscrowAmountAllowed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dueDate, lateFeesDue, autoPaymentDueDate, webPaymentOverride]);

  // Helper function to safely convert values to numbers
  const safeToNumber = (value) => {
    if (value === null || value === undefined || value === "") {
      return 0;
    }
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  // Built from local date parts rather than toISOString, which converts to UTC and
  // reports the previous day for anyone west of Greenwich.
  const toPostingDateString = (date) =>
    `${date.getFullYear()}-${`0${date.getMonth() + 1}`.slice(-2)}-${`0${date.getDate()}`.slice(
      -2,
    )}`;

  // A recurring payment is a schedule rather than a single date, so it is quoted
  // against the first instalment the borrower is agreeing to: the chosen day of the
  // chosen month, rolled into next year if that month has already passed.
  const firstRecurringPostingDate = () => {
    const scheduled = new Date(
      today.getFullYear(),
      selectedMonth - 1,
      selectedDay,
    );

    return scheduled < today
      ? new Date(today.getFullYear() + 1, selectedMonth - 1, selectedDay)
      : scheduled;
  };

  const buildFeeQuoteBody = () => {
    if (type === ESCROW_SHORTAGE) {
      return {
        loanId: activeLoan,
        paymentMethodId: selectedMethod,
        paymentType: type,
        postingDate: toPostingDateString(startDate),
        paymentAmount: safeToNumber(escrowShortageAmount),
      };
    }

    if (type === RECURRING_PAYMENT) {
      return {
        loanId: activeLoan,
        paymentMethodId: selectedMethod,
        paymentType: RECURRING_PAYMENT_TYPE_LABEL,
        postingDate: toPostingDateString(firstRecurringPostingDate()),
        paymentAmount: safeToNumber(amountDue),
        additionalPrincipalAmount: safeToNumber(additionalPrincipal),
        additionalEscrowAmount: safeToNumber(additionalEscrow),
      };
    }

    return {
      loanId: activeLoan,
      paymentMethodId: selectedMethod,
      paymentType: type,
      // Sent as the borrower chose it, not advanced past the cut-off. The service
      // applies its own adjustment before pricing, which is the whole reason the two
      // used to disagree.
      postingDate: toPostingDateString(startDate),
      paymentAmount: safeToNumber(amount),
      lateFeesAmount: safeToNumber(lateFees),
      feesAmount: safeToNumber(fees),
      additionalPrincipalAmount: safeToNumber(additionalPrincipal),
      additionalEscrowAmount: safeToNumber(additionalEscrow),
    };
  };

  useEffect(() => {
    async function processInputs() {
      const actualAmount =
        type === RECURRING_PAYMENT
          ? amountDue
          : type === ESCROW_SHORTAGE
          ? escrowShortageAmount
          : amount;

      const safeActualAmount = safeToNumber(actualAmount);
      const safeAdditionalPrincipal = safeToNumber(additionalPrincipal);
      const safeAdditionalEscrow = safeToNumber(additionalEscrow);
      const safeLateFees = safeToNumber(lateFees);
      const safeFees = safeToNumber(fees);
      const safeTransactionFee = safeToNumber(transactionFee);

      if (
        (!actualAmount || safeActualAmount === 0) &&
        safeAdditionalPrincipal > 0
      ) {
        setIsPrincipalOnlyPayment(true);
      } else {
        setIsPrincipalOnlyPayment(false);
      }

      const totalAmount =
        type === RECURRING_PAYMENT
          ? safeActualAmount +
            safeAdditionalPrincipal +
            safeAdditionalEscrow +
            safeTransactionFee
          : type === ONE_TIME_PAYMENT
          ? safeActualAmount +
            safeLateFees +
            safeFees +
            safeAdditionalPrincipal +
            safeAdditionalEscrow +
            safeTransactionFee
          : safeActualAmount + safeTransactionFee;

      // Format total amount for display in confirmation dialog only
      const formattedTotalAmount = formatCurrency(totalAmount);

      const dialogBody = `Are you sure you want to ${
        type === RECURRING_PAYMENT
          ? mode === "new"
            ? "set a recurring payment of"
            : "edit the recurring payment of"
          : "pay"
      } ${formattedTotalAmount} ${
        type === RECURRING_PAYMENT
          ? mode === "new"
            ? "starting"
            : "to now start"
          : ""
      } on ${
        type === RECURRING_PAYMENT
          ? `${
              availableDays.find((item) => item.value === selectedDay)?.label
            } ${MONTHS[selectedMonth - 1]?.label}`
          : startDate.toDateString()
      }${
        mode === "new"
          ? ` using ${
              paymentMethods.find(
                (item) => item.paymentMethodId === selectedMethod,
              )?.nickname
            }`
          : ""
      }?`;

      setAmountTotal(totalAmount);
      setConfirmationDialogBody(dialogBody);

      const mandatoryFields = [
        actualAmount,
        lateFees,
        fees,
        additionalPrincipal,
        additionalEscrow,
      ];
      const errorMessages = [
        amountErrorMessage,
        lateFeesErrorMessage,
        feesErrorMessage,
        additionalPrincipalErrorMessage,
        additionalEscrowErrorMessage,
      ];

      const emptyFields = !mandatoryFields.some(
        (item) => item && safeToNumber(item) !== 0,
      );
      const hasErrorMessages = errorMessages.some((item) => item !== "");
      const aboveMaxTransaction = isAmountAboveMax(totalAmount);
      const paymentLessThanPrincipalBalance = isPaymentLessThanPrincipalBalance(
        amount,
        additionalPrincipal,
      );

      setSubmitDisabled(
        emptyFields ||
          hasErrorMessages ||
          feeQuoteFailed ||
          feeQuotePending ||
          (termsAndConditionsText && !termsAndConditionsCheckbox) ||
          (type === ONE_TIME_PAYMENT && aboveMaxTransaction) ||
          selectedMethod === "empty" ||
          (type === ONE_TIME_PAYMENT && !oneTimePaymentAllowed) ||
          (type === RECURRING_PAYMENT && !recurringPaymentAllowed) ||
          !paymentLessThanPrincipalBalance,
      );
    }
    processInputs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    amount,
    amountDue,
    lateFees,
    fees,
    additionalPrincipal,
    additionalEscrow,
    transactionFee,
    type,
    availableDays,
    paymentMethods,
    selectedMethod,
    startDate,
    selectedDay,
    selectedMonth,
    amountErrorMessage,
    lateFeesErrorMessage,
    feesErrorMessage,
    additionalPrincipalErrorMessage,
    additionalEscrowErrorMessage,
    oneTimePaymentAllowed,
    recurringPaymentAllowed,
    termsAndConditionsCheckbox,
    termsAndConditionsText,
    customerMaxTransaction,
    stateMaxTransaction,
    stateMaxFee,
    hasTransactionFee,
    transactionFeeOption,
    edittedFields,
    feeQuoteFailed,
    feeQuotePending,
    activeLoanDetails?.loanDetails?.dueDate,
  ]);

  // Bug 1168698: quote the transaction fee from the payments service instead of
  // recomputing it here.
  //
  // The portal used to reimplement all four fee options, and the two implementations
  // disagreed in five separate ways: the posting date the service advances past the
  // cut-off time and holidays, the timezone the day count is taken in, the threshold
  // boundary (< against <=), the percentage rounding mode (HALF_EVEN against half up),
  // and the zero padding of the fee-waiver routing number. A disagreement was invisible
  // in production, because the service recalculates the fee on submit, bills its own
  // figure and only writes a log warning, so the borrower agreed to one number and was
  // charged another.
  //
  // Quoting is debounced because it runs on every amount and date keystroke, and the
  // reply is discarded if the inputs have moved on, so a slow response cannot overwrite
  // a newer one.
  useEffect(() => {
    // selectedMethod is "" until getPaymentMethods resolves, and the placeholder id
    // when the borrower has none on file. Quoting against either sends an empty
    // paymentMethodId, which the service rejects with "paymentMethodId is mandatory"
    // and which would then disable the submit button for a fault of the portal's own
    // making. The fee stays at zero until there is something real to price against.
    const noPaymentMethodYet =
      !selectedMethod ||
      selectedMethod === EMPTY_PAYMENT_METHODS_FALLBACK[0].paymentMethodId;

    // The escrow shortage charge is configured separately from the four fee options, so
    // it is quoted whatever PAYMENTS_ONLINE_FEE says. The service reads the same two
    // escrow properties the portal used to read for itself and returns zero when the
    // charge is switched off, which is the answer the portal used to work out locally.
    const feeApplies = hasTransactionFee || type === ESCROW_SHORTAGE;

    if (!feeApplies || !activeLoan || !token || noPaymentMethodYet) {
      setTransactionFee(0);
      setFeeQuoteFailed(false);
      setFeeQuotePending(false);
      setFeeQuoteMessage("");
      return undefined;
    }

    // Set before the debounce rather than alongside the request, so the button is
    // already disabled while the borrower is still typing and the fee below is stale.
    setFeeQuotePending(true);

    let discarded = false;
    const timer = setTimeout(() => {
      postPaymentQuote(token, buildFeeQuoteBody(), impersonatedUser)
        .then((quote) => {
          if (discarded) {
            return;
          }
          setTransactionFee(safeToNumber(quote?.processingFeesAmount));
          setFeeQuoteFailed(false);
          setFeeQuoteMessage("");
          setFeeQuotePending(false);
        })
        .catch((error) => {
          if (discarded) {
            return;
          }
          // Leaving the previous amount on screen would show a fee the borrower will
          // not be charged, so the payment is blocked until a quote succeeds. Where the
          // service rejected the payment itself it said why, and saying nothing would
          // grey out the button over a problem the borrower could have corrected.
          setTransactionFee(0);
          setFeeQuoteFailed(true);
          setFeeQuoteMessage(error?.validationMessage ?? "");
          setFeeQuotePending(false);
        });
    }, FEE_QUOTE_DEBOUNCE_MS);

    return () => {
      discarded = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    type,
    activeLoan,
    selectedMethod,
    hasTransactionFee,
    amount,
    amountDue,
    escrowShortageAmount,
    lateFees,
    fees,
    additionalPrincipal,
    additionalEscrow,
    startDate,
    selectedDay,
    selectedMonth,
    token,
    impersonatedUser,
  ]);

  useEffect(() => {
    if (paymentAmountFill && !helocOrConstructionLoan) {
      setAmount(amountDue);
    } else {
      setAmount("");
    }
    if (lateFeeAmountFill || mandateLateFee) {
      setLateFees(lateFeesDue);
    } else {
      setLateFees("");
    }
    if (feeAmountFill) {
      setFees(feesDue);
    } else {
      setFees("");
    }
  }, [
    paymentAmountFill,
    lateFeeAmountFill,
    feeAmountFill,
    amountDue,
    lateFeesDue,
    feesDue,
    helocOrConstructionLoan,
    mandateLateFee,
  ]);

  useEffect(() => {
    switch (type) {
      case ONE_TIME_PAYMENT:
        setTermsAndConditionsText(oneTimePaymentTermsAndConditionsText);
        setTermsAndConditionsElementKey(
          NEW_ONE_TIME_PAYMENT_PAGE_TERMS_AND_CONDITIONS_KEY,
        );
        return;
      case RECURRING_PAYMENT:
        setTermsAndConditionsText(recurringPaymentTermsAndConditionsText);
        setTermsAndConditionsElementKey(
          NEW_RECURRING_PAYMENT_PAGE_TERMS_AND_CONDITIONS_KEY,
        );
        return;
      case ESCROW_SHORTAGE:
        setTermsAndConditionsText(escrowShortagePaymentTermsAndConditionsText);
        setTermsAndConditionsElementKey(
          NEW_ESCROW_SHORTAGE_PAGE_TERMS_AND_CONDITIONS_KEY,
        );
        return;
      default:
        return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    type,
    oneTimePaymentTermsAndConditionsText,
    recurringPaymentTermsAndConditionsText,
    escrowShortagePaymentTermsAndConditionsText,
  ]);

  const isPaymentLessThanPrincipalBalance = (amount, additionalPrincipal) => {
    if (
      safeToNumber(amount) + safeToNumber(additionalPrincipal) >
      activeLoanDetails?.loanDetails?.mortgageBalance
    ) {
      return false;
    }
    return true;
  };

  const resetAdditionalFields = () => {
    setAdditionalPrincipal("");
    setAdditionalEscrow("");
  };

  const callPaymentInfoApi = async (token) => {
    addPendingRequest();

    if (activeLoan === "AdminLoan") {
      const paymentInfoData = { ...AdminPaymentPrevalidateResponse };
      setPaymentInfoDataStates(paymentInfoData);

      const autoPaymentInfoData = { ...AdminAutoPaymentPrevalidateResponse };
      setAutoPaymentInfoDataStates(autoPaymentInfoData);
    } else if (activeLoan !== "no-loan") {
      const paymentInfoData = await getPaymentInfo(
        token,
        activeLoan,
        impersonatedUser,
      );
      setPaymentInfoDataStates(paymentInfoData);

      const autoPaymentInfoData = await getAutoPaymentInfo(
        token,
        activeLoan,
        impersonatedUser,
      );
      setAutoPaymentInfoDataStates(autoPaymentInfoData);
    } else {
      // No-loan snackbar is shown at Payments layout level (Payments.js)
      setSnackbarMessage(NO_LOAN_REGISTERED_WARNING_MESSAGE);
      setSnackbarSeverity("warning");
    }

    const paymentMethodsData = await getPaymentMethods(token, impersonatedUser);
    setPaymentMethodsDataStates(paymentMethodsData);

    removePendingRequest();
  };

  const setPaymentInfoDataStates = (data) => {
    if (!data.message) {
      setOneTimePaymentAllowed(data.paymentAllowed);
      setAmountDue(data.amountDue || 0);
      setLateFeesDue(data.lateFees || 0);
      setFeesDue(data.fees || 0);
      if (data.postingDate) {
        setDueDate(utcToZonedTime(data.postingDate, userTimeZone));
      }
    } else {
      setOneTimePaymentAllowed(false);
    }
  };

  const setAutoPaymentInfoDataStates = (data) => {
    if (!data.message) {
      setRecurringPaymentAllowed(data.paymentAllowed);
      setAutoPaymentDueDate(
        utcToZonedTime(data.autopaymentDueDate, userTimeZone),
      );
      if (state?.from === "activity") {
        setAdditionalEscrow(data.additionalEscrow);
        setAdditionalPrincipal(data.additionalPrincipal);
      }
    } else {
      setRecurringPaymentAllowed(false);
    }
  };

  const setPaymentMethodsDataStates = (data) => {
    const activePaymentMethods = data?.length
      ? data.filter((method) => method.paymentMethodId)
      : [];
    if (activePaymentMethods?.length > 0) {
      setPaymentMethods(activePaymentMethods);
      setSelectedMethod(activePaymentMethods[0].paymentMethodId);
      setDisabledPaymentMethod(false);
    } else {
      setPaymentMethods(EMPTY_PAYMENT_METHODS_FALLBACK);
      setSelectedMethod(EMPTY_PAYMENT_METHODS_FALLBACK[0].paymentMethodId);
      setDisabledPaymentMethod(true);
    }
  };

  const setPaymentTypeAllowed = () => {
    // If online payments are not allowed, disable recurring payments online
    if (!paymentsOnlineAllowed) {
      setRecurringPaymentsOnlineAllowed(false);
    }

    // Ensure one-time payments are allowed if online payments are allowed
    if (paymentsOnlineAllowed) {
      setOneTimePaymentAllowed(true);
    }

    // Ensure recurring payments are allowed if online payments and auto-debit are allowed
    if (paymentsOnlineAllowed && recurringPaymentsOnlineAllowed) {
      setRecurringPaymentAllowed(true);
    }
  };

  const fillConfiguratorFields = (fields) => {
    fields.forEach((item) => {
      switch (item.key) {
        case NEW_PAYMENT_PAGE_DESCRIPTION_KEY:
          setDescription(item.value);
          return;
        case NEW_PAYMENT_PAGE_FOOTER_NOTE_KEY:
          setFooterNote(item.value);
          return;
        case NEW_ONE_TIME_PAYMENT_PAGE_TERMS_AND_CONDITIONS_KEY:
          setOneTimePaymentTermsAndConditionsText(item.value);
          return;
        case NEW_RECURRING_PAYMENT_PAGE_TERMS_AND_CONDITIONS_KEY:
          setRecurringPaymentTermsAndConditionsText(item.value);
          return;
        case NEW_ESCROW_SHORTAGE_PAGE_TERMS_AND_CONDITIONS_KEY:
          setEscrowShortageTermsAndConditionsText(item.value);
          return;
        case PAYMENTS_ONLINE_ALLOWED_KEY:
          setPaymentsOnlineAllowed(item.value ? item.value === "true" : true);
          return;
        case PAYMENTS_ONLINE_PAYMENT_AMOUNT_FILL_KEY:
          setPaymentAmountFill(+item.value);
          return;
        case PAYMENTS_ONLINE_LATE_FEE_FILL_KEY:
          setLateFeeAmountFill(+item.value);
          return;
        case PAYMENTS_ONLINE_OTHER_FEE_FILL_KEY:
          setFeeAmountFill(+item.value);
          return;
        case PAYMENTS_ONLINE_STATE_TRANSACTION_MAX_KEY:
          applyStateMax(item.value, setStateMaxTransaction);
          return;
        case PAYMENTS_ONLINE_STATE_FEE_MAX_KEY:
          applyStateMax(item.value, setStateMaxFee);
          return;
        case PAYMENTS_ONLINE_FEE_KEY:
          setHasTransactionFee(item.value === "true");
          return;
        case PAYMENTS_ONLINE_FEE_OPTION_KEY:
          setTransactionFeeOption(item.value);
          return;
        case PAYMENTS_MANDATE_LATE_FEE_KEY:
          setMandateLateFee(item.value === "true");
          return;
        case AUTO_PAYMENTS_ONLINE_ALLOW_AUTO_DEBIT_KEY:
          setRecurringPaymentsOnlineAllowed(item.value === "true");
          return;
        case AUTO_PAYMENTS_ONLINE_LATEST_DAY_AUTO_DEBIT_KEY:
          setLatestAutoDebitDate(parseInt(item.value, 10));
          return;
        case AUTO_PAYMENTS_ONLINE_EARLIEST_DAY_AUTO_DEBIT_KEY:
          setEarliestAutoDebitDate(parseInt(item.value, 10));
          return;
        case AUTO_PAYMENTS_ONLINE_ALLOW_DELINQUENT_LOANS_KEY:
          setAllowAutoDebitForDelinquentLoans(item.value === "true");
          return;
        case PAYMENTS_ONLINE_CUSTOMER_TRANSACTION_KEY:
          checkCustomerMax(item.value === "true");
          return;
        case PAYMENTS_ONLINE_PRINCIPAL_TRANSACTION_MAX_KEY:
          setPrincipalMaxTransaction(item.value || null);
          return;
        case PAYMENTS_ONLINE_CUTOFF_TIME_KEY:
          checkPaymentTime(item.value);
          return;
        case PAYMENTS_ONLINE_POSTING_DATE_KEY:
          setPostingDateDays(item.value);
          return;
        case PAYMENTS_ONLINE_DAYS_UNTIL_DELINQUENT_KEY:
          setDelinquentDays(item.value);
          return;
        case PAYMENTS_ONLINE_LATE_CHARGE_THRESHOLD_KEY:
          setLateChargeThreshold(item.value);
          return;
        case PAYMENTS_ONLINE_ALLOW_PRINCIPAL_IF_DELINQUENT_KEY:
          setPrincipalIfDelinquent(item.value === "true");
          return;
        case PAYMENTS_ONLINE_MAX_DAYS_INTO_FUTURE_KEY:
          setMaxDaysIntoFutureForPayment(item.value);
          return;
        case PAYMENTS_ONLINE_FEE_CLIENT_ROUTING_NO_KEY:
          setPaymentFeeRoutingNo(item.value);
          return;
        default:
          return;
      }
    });
  };

  const changeTermsAndConditionsText = (text) => {
    setTermsAndConditionsText(text);
    switch (type) {
      case ONE_TIME_PAYMENT:
        setOneTimePaymentTermsAndConditionsText(text);
        return;
      case RECURRING_PAYMENT:
        setRecurringPaymentTermsAndConditionsText(text);
        return;
      default:
        return;
    }
  };

  const checkDelinquentRecurringAllowed = () => {
    if (differenceInCalendarDays(today, dueDate) < 0) {
      // Not yet due - always allowed.
      setDelinquentLoanAutoDebitAllowed(true);
    } else {
      // Delinquent - gated only by admin config; Elevate also enforces its own stops.
      setDelinquentLoanAutoDebitAllowed(allowAutoDebitForDelinquentLoans);
    }
  };

  const checkDelinquentPrincipalAllowed = () => {
    if (
      !principalIfDelinquent &&
      differenceInCalendarDays(today, dueDate) > 0
    ) {
      setDelinquentLoanPrincipalAllowed(false);
      return;
    }
    setDelinquentLoanPrincipalAllowed(true);
  };

  const checkDelinquentPaymentAllowed = () => {
    // Web Payment Override (DICT WPOSTOP) - the service skips the delinquency
    // and late-charge checks for this loan, so blocking here would stop the
    // request ever reaching it.
    if (webPaymentOverride) {
      setDelinquentLoanPaymentAllowed(true);
      return;
    }
    if (
      (delinquentDays.toString().length > 0 &&
        differenceInCalendarDays(today, dueDate) >=
          safeToNumber(delinquentDays)) ||
      (safeToNumber(lateChargeThreshold) > 0 &&
        safeToNumber(lateFeesDue) > safeToNumber(lateChargeThreshold))
    ) {
      setDelinquentLoanPaymentAllowed(false);
      return;
    }
    setDelinquentLoanPaymentAllowed(true);
  };

  const checkAdditionalEscrowAmountAllowed = () => {
    if (activeLoanDetails?.loanDetails?.escrowLoan) {
      setAdditionalEscrowAmountAllowed(true);
    } else {
      setAdditionalEscrowAmountAllowed(false);
    }
  };
  const prefillPostingDate = async (dateSelection) => {
    if (dateSelection.length > 0) {
      const calendarDaysDifference = differenceInCalendarDays(
        addBusinessDays(today, +dateSelection),
        today,
      );
      const holidayArray = await getHolidays(
        token,
        calendarDaysDifference + 1,
        impersonatedUser,
      );
      setStartDate(
        addBusinessDays(today, +dateSelection + holidayArray.length),
      );
      setMinDate(addBusinessDays(today, +dateSelection + holidayArray.length));
    }
  };

  const checkPaymentTime = (configuratorTime) => {
    if (isSameDay(today, startDate)) {
      const todayAtCutoffTime = zonedTimeToUtc(
        `${today.getFullYear()}-${(today.getMonth() + 1)
          .toString()
          .padStart(2, "0")}-${today.getDate().toString().padStart(2, "0")} ${
          configuratorTime || "20:00"
        }`,
        "America/New_York",
      );

      if (differenceInCalendarDays(todayAtCutoffTime, today) === 0) {
        const configuratorHour = todayAtCutoffTime.getHours();
        const configuratorMinutes = todayAtCutoffTime.getMinutes();
        setCutoffTime(
          `${configuratorHour.toString().padStart(2, "0")}:${configuratorMinutes
            .toString()
            .padStart(2, "0")}`,
        );
        if (
          today.getHours() > +configuratorHour ||
          (today.getHours() === +configuratorHour &&
            today.getMinutes() > configuratorMinutes)
        ) {
          setCutoffHourPaymentAllowed(false);
          return;
        }
      }
      setCutoffHourPaymentAllowed(true);
    } else {
      setCutoffHourPaymentAllowed(true);
    }
  };

  const isAmountAboveMax = (totalAmount) => {
    const lowestMax = [stateMaxTransaction, customerMaxTransaction]
      .filter((item) => item !== null)
      .sort((a, b) => safeToNumber(a) - safeToNumber(b));
    if (lowestMax?.[0]) {
      return safeToNumber(lowestMax[0]) < safeToNumber(totalAmount);
    }
    return false;
  };

  const checkCustomerMax = (hasMax) => {
    if (hasMax) {
      const maxValue = edittedFields.find(
        (item) => item.key === PAYMENTS_ONLINE_CUSTOMER_TRANSACTION_MAX_KEY,
      );
      if (maxValue && maxValue.value) {
        setCustomerMaxTransaction(+maxValue.value);
      } else {
        setCustomerMaxTransaction(null);
      }
    } else {
      setCustomerMaxTransaction(null);
    }
  };

  const applyStateMax = (stateConfig, setter) => {
    const stateConfigSplit = stateConfig.split(";");
    const currentStateConfigField = stateConfigSplit.find(
      (item) =>
        item.split("=")[0] === activeLoanDetails?.borrowerDetails?.state,
    );
    if (currentStateConfigField) {
      setter(currentStateConfigField.split("=")[1]);
    }
  };

  const buildNewPaymentRequestBody = async () => {
    let actualDate = startDate;
    if (!cutoffHourPaymentAllowed) {
      const calendarDaysDifference = differenceInCalendarDays(
        addBusinessDays(startDate, 1),
        startDate,
      );
      const holidayArray = await getHolidays(
        token,
        calendarDaysDifference + 1,
        impersonatedUser,
      );
      actualDate = addBusinessDays(startDate, 1 + holidayArray.length);
    }
    const dateString = `${actualDate.getFullYear()}-${`0${
      actualDate.getMonth() + 1
    }`.slice(-2)}-${`0${actualDate.getDate()}`.slice(-2)}`;
    return type === RECURRING_PAYMENT
      ? {
          loanId: activeLoan,
          paymentMethodId: selectedMethod,
          additionalPrincipalAmount: safeToNumber(additionalPrincipal),
          additionalEscrowAmount: safeToNumber(additionalEscrow),
          postingDay: selectedDay,
          postingStartMonth: selectedMonth,
        }
      : type === ONE_TIME_PAYMENT
      ? {
          loanId: activeLoan,
          paymentMethodId: selectedMethod,
          paymentType: type,
          postingDate: dateString,
          paymentAmount: safeToNumber(amount),
          lateFeesAmount: safeToNumber(lateFees),
          feesAmount: safeToNumber(fees),
          processingFeesAmount: safeToNumber(transactionFee),
          additionalPrincipalAmount: safeToNumber(additionalPrincipal),
          additionalEscrowAmount: safeToNumber(additionalEscrow),
        }
      : {
          loanId: activeLoan,
          paymentMethodId: selectedMethod,
          paymentType: type,
          postingDate: dateString,
          paymentAmount: +escrowShortageAmount,
          processingFeesAmount: safeToNumber(transactionFee),
        };
  };

  const sendPayment = (requestBody) => {
    addPendingRequest();
    if (type === ONE_TIME_PAYMENT || type === ESCROW_SHORTAGE) {
      postPayment(token, requestBody, impersonatedUser).then((data) => {
        removePendingRequest();
        if (data.message) {
          setSnackbarMessage(capitalizeFirstLetterOnly(data.message));
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        } else if (data?.paymentAccepted === false) {
          setSnackbarMessage(
            data.friendlyErrorMessage
              ? capitalizeFirstLetterOnly(data.friendlyErrorMessage)
              : GENERIC_ERROR_MESSAGE,
          );
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        } else if (data?.paymentAccepted === true) {
          console.log("Payment successful:", data);
          setPaymentConfirmationNumber(data?.confirmationNumber);
          setOpenPaymentConfirmation(true);
        }
      });
    } else {
      if (mode === "new") {
        postRecurringPayment(token, requestBody, impersonatedUser).then(
          (data) => {
            removePendingRequest();
            if (data.message) {
              setSnackbarMessage(capitalizeFirstLetterOnly(data.message));
              setSnackbarSeverity("error");
              setSnackbarOpen(true);
            } else if (data?.success === false) {
              setSnackbarMessage(
                data.friendlyErrorMessage
                  ? capitalizeFirstLetterOnly(data.friendlyErrorMessage)
                  : GENERIC_ERROR_MESSAGE,
              );
              setSnackbarSeverity("error");
              setSnackbarOpen(true);
            } else if (data?.success === true) {
              navigate("/payments/activity", { replace: true });
            }
          },
        );
      } else {
        const editBody = {
          postingDay: requestBody.postingDay,
          postingStartMonth: requestBody.postingStartMonth,
        };
        putRecurringPayment(
          token,
          requestBody.loanId,
          editBody,
          impersonatedUser,
        ).then((data) => {
          removePendingRequest();
          if (data.message) {
            setSnackbarMessage(capitalizeFirstLetterOnly(data.message));
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
          } else if (data?.success === false) {
            setSnackbarMessage(
              data.friendlyErrorMessage
                ? capitalizeFirstLetterOnly(data.friendlyErrorMessage)
                : GENERIC_ERROR_MESSAGE,
            );
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
          } else if (data?.success === true) {
            navigate("/payments/activity", { replace: true });
          }
        });
      }
    }
  };

  const handleConfirmationClose = async () => {
    const requestBody = await buildNewPaymentRequestBody();
    sendPayment(requestBody);
    setConfirmationOpen(false);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbarOpen(false);
  };

  const disableFields = () => {
    // If online payments are not allowed, disable all fields
    if (!paymentsOnlineAllowed) {
      return true;
    }

    // For recurring payments
    if (type === RECURRING_PAYMENT) {
      // Check if recurring payments online are allowed
      if (!recurringPaymentsOnlineAllowed) {
        return true;
      }
      // Check if recurring payments are allowed for this loan
      if (!recurringPaymentAllowed) {
        return true;
      }
      // Check if auto-debit is allowed for delinquent loans
      return !delinquentLoanAutoDebitAllowed;
    }

    // For one-time payments
    if (type !== RECURRING_PAYMENT) {
      // Check if one-time payments are allowed
      return !oneTimePaymentAllowed;
    }

    // Default case - disable fields
    return true;
  };

  const closePaymentConfirmationDialog = () => {
    setOpenPaymentConfirmation(false);
    navigate("/payments/activity", { replace: true });
  };

  if (shouldUsePnmMakePaymentView && showPnmMissingSmartlinkView) {
    const activeLoanId =
      activeLoanDetails?.loanDetails?.loanId ?? getLoanId(activeLoan);
    const activeLoanNumber = getDisplayLoanNumber(
      activeLoanId,
      activeLoanDetails,
    );

    return (
      <FlexContainer
        maxWidth={false}
        disableGutters
        flexdirection="column"
        alignitems="flex-start"
        width="100%"
      >
        <Typography
          sx={{
            background: theme.palette.warning.light,
            width: "100%",
            padding: "5px 25px",
            border: `2px solid ${theme.palette.warning.main}`,
            borderRadius: "5px",
            textAlign: "center",
          }}
        >
          {getPnmSmartLinkNotFoundMessage(activeLoanNumber)}
        </Typography>
      </FlexContainer>
    );
  }

  if (shouldUsePnmMakePaymentView && showPnmPaymentView) {
    const pnmSubmitDisabled =
      !!impersonatedUser ||
      userRoles?.includes(CLIENT_ADMIN_ROLE) ||
      !pnmSmartlink ||
      pnmLoadError;

    const handlePnmMakePayment = () => {
      if (!pnmSmartlink) {
        return;
      }
      window.open(pnmSmartlink, "_blank", "noopener,noreferrer");
    };

    return (
      <FlexContainer
        maxWidth={false}
        disableGutters
        flexdirection="column"
        alignitems="flex-start"
        width="100%"
        sx={{ gap: { xs: "25px", md: "1.25rem" } }}
      >
        {(userRoles?.includes(CLIENT_ADMIN_ROLE) &&
          visibleRole === CLIENT_ADMIN_ROLE) ||
        description ? (
          <EditableText
            renderedText={description}
            setRenderedText={setDescription}
            elementKey={NEW_PAYMENT_PAGE_DESCRIPTION_KEY}
            wasModified={findElementIncludedInArray(
              edittedFields,
              "modified",
              true,
              "key",
              NEW_PAYMENT_PAGE_DESCRIPTION_KEY,
            )}
            padding="0 0 25px 0"
            fontSize={12}
            withHyperlink
          />
        ) : (
          ""
        )}
        <PayNearMePayment
          token={token}
          loanId={getLoanId(activeLoan)}
          impersonatedUser={impersonatedUser}
          onLoadErrorChange={setPnmLoadError}
        />
        {!pnmLoadError && (
          <>
            <Box
              sx={{
                width: { xs: "100%", md: "33%" },
                mx: "auto",
              }}
            >
              <PillButton
                color="primary"
                variant="contained"
                width="100%"
                customfontsize="1.25rem"
                onClick={handlePnmMakePayment}
                disabled={pnmSubmitDisabled}
                endIcon={<OpenInNewIcon sx={{ fontSize: "1.1rem" }} />}
              >
                Make a Payment
              </PillButton>
            </Box>
            <Box
              sx={{
                width: { xs: "100%", md: "66%" },
                mx: "auto",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Typography
                fontWeight="bold"
                sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
              >
                {PNM_SMART_LINK_URL_LABEL}
              </Typography>
              <TextField
                fullWidth
                value={pnmSmartlink}
                InputProps={{ readOnly: true }}
                variant="outlined"
                size="small"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: theme.palette.common.white,
                  },
                }}
              />
            </Box>
          </>
        )}
      </FlexContainer>
    );
  }

  // Default Make a Payment form (PAYMENTS_PNM_INTEGRATION_ALLOWED=false, edit mode, or invalid smart link)
  return (
    <FlexContainer
      flexdirection="column"
      alignitems="flex-start"
      width="100%"
      sx={{
        gap: { xs: "25px", md: "1.25rem" },
        "& > div": {
          width: { xs: "100%", md: "100%" },
          boxShadow: { md: "none" },
          background: { md: "unset" },
          borderRadius: { md: "unset" },
        },
      }}
    >
      {!paymentsOnlineAllowed ||
      (!recurringPaymentsOnlineAllowed && type === RECURRING_PAYMENT) ||
      (!delinquentLoanAutoDebitAllowed && type === RECURRING_PAYMENT) ||
      (!oneTimePaymentAllowed && type !== RECURRING_PAYMENT) ||
      (!recurringPaymentAllowed && type === RECURRING_PAYMENT) ||
      !cutoffHourPaymentAllowed ||
      !delinquentLoanPaymentAllowed ? (
        <Typography
          sx={{
            background: theme.palette.warning.light,
            width: "100%",
            padding: "5px 25px",
            marginBottom: "25px",
            border: `2px solid ${theme.palette.warning.main}`,
            borderRadius: "5px",
            textAlign: "center",
          }}
        >
          {warningMessage}
        </Typography>
      ) : (
        ""
      )}
      {feeQuoteMessage ? (
        <Typography
          sx={{
            background: theme.palette.warning.light,
            width: "100%",
            padding: "5px 25px",
            marginBottom: "25px",
            border: `2px solid ${theme.palette.warning.main}`,
            borderRadius: "5px",
            textAlign: "center",
          }}
        >
          {feeQuoteMessage}
        </Typography>
      ) : (
        ""
      )}
      {(userRoles?.includes(CLIENT_ADMIN_ROLE) &&
        visibleRole === CLIENT_ADMIN_ROLE) ||
      description ? (
        <EditableText
          renderedText={description}
          setRenderedText={setDescription}
          elementKey={NEW_PAYMENT_PAGE_DESCRIPTION_KEY}
          wasModified={findElementIncludedInArray(
            edittedFields,
            "modified",
            true,
            "key",
            NEW_PAYMENT_PAGE_DESCRIPTION_KEY,
          )}
          padding="0 0 25px 0"
          fontSize={12}
          withHyperlink
        />
      ) : (
        ""
      )}
      <FlexContainer
        padding={isMobile ? "0" : "0 0 50px 0"}
        gap={isMobile ? "12px" : "25px"}
        alignself="flex-start"
        justifycontent="flex-start"
        // sx={{
        //   alignSelf: { xs: "center", md: "flex-start" },
        //   alignItems: { xs: "center", md: "flex-start" },
        //   justifyContent: { xs: "center", md: "flex-start" },
        // }}
      >
        <Typography fontWeight="bold">Type of payment</Typography>
        <FormControl variant="standard">
          <Select
            value={type}
            onChange={(e) => setType(e.target.value)}
            disabled={mode === "edit"}
          >
            <MenuItem value={ONE_TIME_PAYMENT}>One Time Payment</MenuItem>
            <MenuItem value={RECURRING_PAYMENT}>Recurring Payment</MenuItem>
            {escrowShortage ? (
              <MenuItem value={ESCROW_SHORTAGE}>Escrow Shortage</MenuItem>
            ) : null}
          </Select>
        </FormControl>
      </FlexContainer>
      <CardMedium>
        <FlexContainer
          width="100%"
          flexdirection="column"
          gap={isMobile ? "12px" : "25px"}
          padding={isMobile ? "18px" : "0"}
          // justifycontent="start"
          // alignitems="start"
          alignself="flex-start"
        >
          <SectionTitle
            variant="h2"
            color="primary"
            sx={{
              alignSelf: "flex-start",
              width: "100%",
              textAlign: "left",
            }}
          >
            Payment Amount
          </SectionTitle>
          <PaymentAmount
            fieldsDisabled={
              (disableFields() && delinquentLoanPaymentAllowed) ||
              mode === "edit"
            }
            selectedType={type}
            dueDate={
              type === ONE_TIME_PAYMENT
                ? dueDate
                : type === RECURRING_PAYMENT
                ? autoPaymentDueDate
                : type === ESCROW_SHORTAGE
                ? dueDate
                : null
            }
            amountDue={
              type === ESCROW_SHORTAGE ? escrowShortageAmount : amountDue
            }
            lateFeesDue={lateFeesDue}
            feesDue={feesDue}
            amount={amount}
            setAmount={setAmount}
            lateFees={lateFees}
            setLateFees={setLateFees}
            fees={fees}
            setFees={setFees}
            additionalPrincipal={additionalPrincipal}
            setAdditionalPrincipal={setAdditionalPrincipal}
            additionalEscrow={additionalEscrow}
            setAdditionalEscrow={setAdditionalEscrow}
            amountErrorMessage={amountErrorMessage}
            setAmountErrorMessage={setAmountErrorMessage}
            lateFeesErrorMessage={lateFeesErrorMessage}
            setLateFeesErrorMessage={setLateFeesErrorMessage}
            feesErrorMessage={feesErrorMessage}
            setFeesErrorMessage={setFeesErrorMessage}
            additionalPrincipalErrorMessage={additionalPrincipalErrorMessage}
            setAdditionalPrincipalErrorMessage={
              setAdditionalPrincipalErrorMessage
            }
            additionalEscrowErrorMessage={additionalEscrowErrorMessage}
            setAdditionalEscrowErrorMessage={setAdditionalEscrowErrorMessage}
            mandateLateFee={mandateLateFee}
            isPrincipalOnlyPayment={isPrincipalOnlyPayment}
            principalMaxTransaction={principalMaxTransaction}
            principalDisabled={!delinquentLoanPrincipalAllowed}
            additionalEscrowAmountAllowed={additionalEscrowAmountAllowed}
            helocOrConstructionLoan={helocOrConstructionLoan}
          />
          {paymentsOnlineAllowed && delinquentLoanPaymentAllowed ? (
            <PaymentTotal
              transactionFee={transactionFee}
              amountTotal={amountTotal}
              maxTransaction={
                [stateMaxTransaction, customerMaxTransaction]
                  .filter((item) => item !== null)
                  .sort((a, b) => +a - +b)?.[0] ?? null
              }
              type={type}
              amount={amount}
              additionalPrincipal={additionalPrincipal}
            />
          ) : (
            ""
          )}
        </FlexContainer>
      </CardMedium>

      <CardMedium>
        <FlexContainer
          width="100%"
          flexdirection="column"
          gap={isMobile ? "12px" : "25px"}
          padding={isMobile ? "18px" : "0px"}
        >
          <SectionTitle
            variant="h2"
            color="primary"
            sx={{
              alignSelf: "flex-start",
              width: "100%",
              textAlign: "left",
            }}
          >
            {type === RECURRING_PAYMENT ? "First Payment" : ""} Posting Date
          </SectionTitle>
          {type === RECURRING_PAYMENT ? (
            <PaymentRecurrence
              availableDays={availableDays}
              unavailableDays={unavailableDays}
              selectedDay={selectedDay}
              setSelectedDay={setSelectedDay}
              selectedMonth={selectedMonth}
              setSelectedMonth={setSelectedMonth}
            />
          ) : (
            <FlexContainer
              padding="0 0 25px 0"
              gap="25px"
              width={isMobile ? "100%" : "auto"}
            >
              <FormItem
                elementWidth={isMobile ? "unset" : "200px"}
                label={
                  isMobile ? "Current Posting Date" : "Current Posting Date"
                }
                inputType="datepicker"
                value={startDate}
                setValue={setStartDate}
                datepickerOpen={datepickerOpen}
                setDatepickerOpen={setDatepickerOpen}
                minDate={minDate}
                maxDate={maxDate}
                filterDate={(date) => !isWeekend(date)}
                excludedDates={excludedDates}
                hasComponent={
                  paymentsOnlineAllowed && delinquentLoanPaymentAllowed
                }
                component={
                  <Box
                    width={isMobile ? "100%" : "200px"}
                    display="flex"
                    justifyContent={isMobile ? "flex-end" : "flex-start"}
                  >
                    <PillButton
                      variant={isMobile ? "text" : "contained"}
                      width={isMobile ? "max-content" : "130px"}
                      color="primary"
                      onClick={(e) => setDatepickerOpen(true)}
                      sx={{
                        fontSize: { xs: "14px", md: "10px" },
                        padding: "5px 15px",
                        fontWeight: { xs: "bold", md: "normal" },
                      }}
                    >
                      Change Posting Date
                    </PillButton>
                  </Box>
                }
              />
            </FlexContainer>
          )}
        </FlexContainer>
      </CardMedium>
      {mode === "new" ? (
        <CardMedium>
          <FlexContainer
            width="100%"
            flexdirection="column"
            gap={isMobile ? "12px" : "25px"}
            padding={isMobile ? "18px" : "0px"}
            justifycontent="center"
            alignitems="center"
          >
            <SectionTitle
              variant="h2"
              color="primary"
              sx={{
                alignSelf: "flex-start",
                width: "100%",
                textAlign: "left",
              }}
            >
              Bank Information
            </SectionTitle>
            <FlexContainer
              padding="0"
              gap="25px"
              flexdirection={isMobile ? "column" : "row"}
              width="100%"
              justifycontent={isMobile ? "flex-start" : "center"}
              alignitems={isMobile ? "flex-start" : "center"}
            >
              <FormItem
                elementWidth={isMobile ? "unset" : "200px"}
                label={isMobile ? "Payment Method" : "Payment Method"}
                inputType="dropdown"
                value={selectedMethod}
                setValue={setSelectedMethod}
                dropdownOptions={paymentMethods}
                dropdownKey="paymentMethodId"
                dropdownValue="paymentMethodId"
                dropdownLabel="nickname"
                disabled={
                  !paymentsOnlineAllowed ||
                  (type === RECURRING_PAYMENT &&
                    !recurringPaymentsOnlineAllowed) ||
                  (type === RECURRING_PAYMENT &&
                    !delinquentLoanAutoDebitAllowed) ||
                  !delinquentLoanPaymentAllowed ||
                  disabledPaymentMethod
                }
                hasComponent
                component={
                  <Box
                    width={isMobile ? "100%" : "200px"}
                    display="flex"
                    justifyContent={isMobile ? "flex-end" : "flex-start"}
                  >
                    <PillButton
                      variant={isMobile ? "text" : "contained"}
                      width={isMobile ? "max-content" : "150px"}
                      color="primary"
                      onClick={(e) => navigate("/payments/methods")}
                      sx={{
                        fontSize: { xs: "14px", md: "10px" },
                        padding: "5px 15px",
                        fontWeight: { xs: "bold", md: "normal" },
                      }}
                    >
                      Manage Payment Methods
                    </PillButton>
                  </Box>
                }
              />
            </FlexContainer>
          </FlexContainer>
        </CardMedium>
      ) : (
        ""
      )}
      {(userRoles?.includes(CLIENT_ADMIN_ROLE) &&
        visibleRole === CLIENT_ADMIN_ROLE) ||
      (paymentsOnlineAllowed &&
        delinquentLoanPaymentAllowed &&
        termsAndConditionsText) ? (
        <EditableTermsAndConditions
          renderedText={termsAndConditionsText}
          setRenderedText={changeTermsAndConditionsText}
          checkboxActive={termsAndConditionsCheckbox}
          setCheckboxActive={setTermsAndConditionsCheckbox}
          elementKey={termsAndConditionsElementKey}
          wasModified={findElementIncludedInArray(
            edittedFields,
            "modified",
            true,
            "key",
            termsAndConditionsElementKey,
          )}
          padding="25px 0"
          fontSize={12}
        />
      ) : (
        ""
      )}
      <PillButton
        color="primary"
        variant="contained"
        width={isMobile ? "100%" : "33%"}
        alignself="center"
        customfontsize="1.25rem"
        onClick={() => setConfirmationOpen(true)}
        disabled={
          !paymentsOnlineAllowed ||
          (type === RECURRING_PAYMENT && !recurringPaymentsOnlineAllowed) ||
          (type === RECURRING_PAYMENT && !delinquentLoanAutoDebitAllowed) ||
          !delinquentLoanPaymentAllowed ||
          submitDisabled ||
          impersonatedUser ||
          userRoles?.includes(CLIENT_ADMIN_ROLE)
        }
      >
        Submit Payment
      </PillButton>
      {(userRoles?.includes(CLIENT_ADMIN_ROLE) &&
        visibleRole === CLIENT_ADMIN_ROLE) ||
      footerNote ? (
        <>
          <Divider sx={{ paddingTop: "50px" }} flexItem />
          <EditableText
            renderedText={footerNote}
            setRenderedText={setFooterNote}
            elementKey={NEW_PAYMENT_PAGE_FOOTER_NOTE_KEY}
            wasModified={findElementIncludedInArray(
              edittedFields,
              "modified",
              true,
              "key",
              NEW_PAYMENT_PAGE_FOOTER_NOTE_KEY,
            )}
            padding="5px 0 0 0"
            fontSize={12}
            withHyperlink
          />
        </>
      ) : (
        ""
      )}
      <ConfirmationDialog
        open={confirmationOpen}
        close={handleConfirmationClose}
        dismiss={() => setConfirmationOpen(false)}
        title="Confirm payment"
        body={confirmationDialogBody}
      />
      <ActionSnackbar
        severity={snackbarSeverity}
        borderColor={theme.palette[snackbarSeverity].main}
        vertical="top"
        horizontal="center"
        width={TOP_CENTER_SNACKBAR_WIDTH}
        isOpen={snackbarOpen}
        closeFn={handleSnackbarClose}
        snackbarLabel={snackbarMessage}
        hasAction={snackbarMessage === NO_LOAN_REGISTERED_WARNING_MESSAGE}
        actionLabel={
          snackbarMessage === NO_LOAN_REGISTERED_WARNING_MESSAGE
            ? "Register Loan"
            : ""
        }
        actionFn={() => navigate("/profile/loan")}
        autoHideDuration={
          snackbarMessage === NO_LOAN_REGISTERED_WARNING_MESSAGE
            ? null
            : 6000
        }
      />
      <ConfirmationDialog
        open={openPaymentConfirmation}
        close={closePaymentConfirmationDialog}
        dismiss={closePaymentConfirmationDialog}
        title={PAYMENT_SUCCESSFUL}
        body={PAYMENT_CONFIRMATION(paymentConfirmationNumber)}
        hasDismissButton={false}
      />
    </FlexContainer>
  );
}
